import type { ReactNode } from "react";

export interface Column<T> {
  key: string;
  header: string;
  render?: (row: T) => ReactNode;
  align?: "left" | "right";
}

interface TableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  emptyText?: string;
  onRowClick?: (row: T) => void;
}

/**
 * House pattern: renders BOTH a desktop table and a mobile stacked-card list
 * from the same column config; CSS media queries (app.css) show only one at
 * a given width. Keeps every list screen (parties/transactions/assets/...)
 * mobile-usable without per-screen responsive markup.
 */
export function Table<T>({ columns, rows, rowKey, emptyText, onRowClick }: TableProps<T>) {
  if (rows.length === 0) {
    return <div className="empty-state">{emptyText ?? "Nothing here yet."}</div>;
  }

  return (
    <>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} className={c.align === "right" ? "num" : undefined}>
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                style={onRowClick ? { cursor: "pointer" } : undefined}
              >
                {columns.map((c) => (
                  <td key={c.key} className={c.align === "right" ? "num" : undefined}>
                    {c.render ? c.render(row) : String((row as Record<string, unknown>)[c.key] ?? "")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="list-cards">
        {rows.map((row) => (
          <div
            className="list-card"
            key={rowKey(row)}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            style={onRowClick ? { cursor: "pointer" } : undefined}
          >
            {columns.map((c) => (
              <div className="row" key={c.key}>
                <span className="k">{c.header}</span>
                <span>{c.render ? c.render(row) : String((row as Record<string, unknown>)[c.key] ?? "")}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </>
  );
}
